import { useCallback, useEffect, useMemo, MutableRefObject } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useFormik, FormikProps } from 'formik'

import {
  campaignPublicSelector,
  emailSelector,
  firstNameSelector,
  lastNameSelector,
  walletAddressSelector,
} from 'primsell-shared-layer/features'

import { OrderActions, StripeActions, orderErrorSelector, clientSecretSelector } from 'features'

import { buyFormValidationSchema } from './validation'
import { CodeTypeE } from 'primsell-shared-layer/types'

export interface IBuyFormValues {
  firstName: string
  lastName: string
  email: string
  amount: string
}

interface IBuyFormControllerProps {
  internalRef: MutableRefObject<FormikProps<IBuyFormValues> | null>
  amount: IBuyFormValues['amount']
  isPaymentSessionExpired: boolean
  handleRenewPaymentSession: () => void
}

interface IBuyFormControllerReturn {
  formik: FormikProps<IBuyFormValues>
  isSubmitDisabled: boolean
  isInputDisabled: boolean
  isAmountFieldDisabled: boolean
  isCampaignLoyalty: boolean
  collectionName: string | undefined
  walletAddress: string | undefined
}

export const useBuyFormController = ({
  internalRef,
  isPaymentSessionExpired,
  handleRenewPaymentSession,
  amount,
}: IBuyFormControllerProps): IBuyFormControllerReturn => {
  const dispatch = useDispatch()

  const walletAddress = useSelector(walletAddressSelector)
  const clientSecret = useSelector(clientSecretSelector)

  const publicCampaignDetails = useSelector(campaignPublicSelector)
  const orderError = useSelector(orderErrorSelector)

  const defaultFirstName = useSelector(firstNameSelector)
  const defaultLastName = useSelector(lastNameSelector)
  const defaultEmail = useSelector(emailSelector)

  const { code } = useParams()

  const handleSubmit = useCallback(
    (values: IBuyFormValues) => {
      if (isPaymentSessionExpired) {
        handleRenewPaymentSession()
      }
      if (code) {
        dispatch(StripeActions.setClientSecret({ clientSecret: '' }))
        dispatch(OrderActions.createOrderRequest({ ...values, amount: +amount, wallet: walletAddress, code }))
      }
    },
    [amount, code, dispatch, handleRenewPaymentSession, isPaymentSessionExpired, walletAddress]
  )

  const INIT_VALUES: IBuyFormValues = useMemo(() => {
    let defaultAmount = ''

    if (
      publicCampaignDetails?.maxAmountPerPurchase &&
      publicCampaignDetails?.codeType === CodeTypeE.LOYALTY &&
      walletAddress
    ) {
      defaultAmount = publicCampaignDetails.maxAmountPerPurchase.toString()
    }

    return {
      firstName: defaultFirstName,
      lastName: defaultLastName,
      email: defaultEmail,
      amount: defaultAmount,
    }
  }, [
    defaultEmail,
    defaultFirstName,
    defaultLastName,
    publicCampaignDetails?.codeType,
    publicCampaignDetails?.maxAmountPerPurchase,
    walletAddress,
  ])

  const formik = useFormik({
    initialValues: INIT_VALUES,
    validationSchema: buyFormValidationSchema({ inStock: publicCampaignDetails?.inStock }),
    onSubmit: handleSubmit,
    innerRef: internalRef,
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
  })

  const isFormFilled = Object.values(formik.values).every((field) => field !== '' && field != null)

  const isSubmitDisabled = useMemo(() => {
    if (!walletAddress) {
      return true
    }

    if (!publicCampaignDetails?.inStock) {
      return true
    }

    if (clientSecret && !isPaymentSessionExpired) {
      return true
    }

    if (!isFormFilled) {
      return true
    }

    return false
  }, [clientSecret, isFormFilled, isPaymentSessionExpired, publicCampaignDetails?.inStock, walletAddress])

  const isInputDisabled = useMemo(() => {
    if (!walletAddress) {
      return true
    }

    if (!publicCampaignDetails?.inStock) {
      return true
    }

    return false
  }, [publicCampaignDetails?.inStock, walletAddress])

  const isAmountFieldDisabled = useMemo(() => {
    if (publicCampaignDetails?.codeType === CodeTypeE.LOYALTY) {
      return true
    }

    return false
  }, [publicCampaignDetails?.codeType])

  const errorMessage = useMemo(() => {
    if (orderError && orderError.includes('NOT_ENOUGH_NFTS_IN_STOCK')) {
      return `The amount is not matching the available items in stock`
    }
    return ''
  }, [orderError])

  const isCampaignLoyalty = useMemo(
    () => publicCampaignDetails?.codeType === CodeTypeE.LOYALTY,
    [publicCampaignDetails?.codeType]
  )

  const collectionName = useMemo(
    () => publicCampaignDetails?.meta?.collectionName ?? '',
    [publicCampaignDetails?.meta?.collectionName]
  )

  useEffect(() => {
    if (formik.errors.amount !== errorMessage) {
      formik.setFieldError('amount', errorMessage)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorMessage])

  return {
    formik,
    isSubmitDisabled,
    isInputDisabled,
    isAmountFieldDisabled,
    isCampaignLoyalty,
    collectionName,
    walletAddress,
  }
}
